import {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    AutocompleteInputChangeReason,
} from "@material-ui/lab";
import { createStyles, makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import { useField } from "formik";
import * as React from "react";

import { RelodeTheme, SelectInput, useDebounce, useRelodeApp } from "../";
import { LocationSearchModeEnum, LocationSearchScopeEnum } from "../../";
import { Location } from "../../domain";
import { mergeClasses } from "../styles/mergeClasses";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                marginBottom: 0,
            },
            title: {
                textTransform: "lowercase",
                color: theme.relode.colors.alternate.main,
                fontWeight: 600,
                marginBottom: 15,
            },
            downshift: {
                margin: theme.spacing(1),
            },
            menuEmptyMessage: {
                height: "auto",
                whiteSpace: "initial",
            },
            input: {
                margin: theme.spacing(2, 0, 2, 0),
            },
        }),
    { name: "LocationSearch" }
);

export type LocationOption = {
    locationName: string;
    locationId: number;
    postalCode?: string;
};

export interface LocationSearchProps {
    className?: string;
    classes?: Partial<ReturnType<typeof useStyles>>;
    onDistanceChange?: (miles: number) => void | Promise<void>;
    onSelect: (locationOption?: LocationOption, location?: Location) => void;
    disableClearable?: boolean;
    id: string;
    name?: string;
    error?: boolean;
    fullWidth?: boolean;
    helperText?: string;
    label: string;
    selectedLocation?: LocationOption;
    searchRadiusInMeters?: number;
    clearInputOnAdd?: boolean;
    scope?: LocationSearchScopeEnum;
    mode?: LocationSearchModeEnum;
    initialSuggestions?: Location[];
}

export const LocationSearch = (props: LocationSearchProps) => {
    const classes = mergeClasses(useStyles({}), props.classes);
    const {
        selectedLocation,
        scope,
        clearInputOnAdd,
        onSelect,
        onDistanceChange,
        label,
        error,
        helperText,
        disableClearable,
        initialSuggestions,
        className,
        mode = LocationSearchModeEnum.CityState,
    } = props;

    const {
        state: {
            config: { locationLookupService },
        },
    } = useRelodeApp();

    const [suggestions, setSuggestions] = React.useState<LocationOption[]>(
        initialSuggestions ?? []
    );
    const [locations, setLocations] = React.useState<Location[]>([]);
    const [locationSearchText, setLocationSearchText] = React.useState<string>(
        selectedLocation?.locationName ?? ""
    );

    React.useEffect(() => {
        setLocationSearchText(selectedLocation?.locationName ?? "");
    }, [selectedLocation]);

    const debounceSearchLocations = useDebounce(
        async (searchText: string) => {
            if (
                !searchText ||
                searchText.trim() == "" ||
                searchText.trim().length === 0
            ) {
                return initialSuggestions ?? [];
            }
            return await locationLookupService.loadSuggestions(
                searchText,
                scope,
                mode
            );
        },
        500,
        []
    );

    const handleInputChange = async (
        event: never,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (reason === "reset") {
            return;
        }
        setLocationSearchText(value);
        const locations = await debounceSearchLocations(value);
        setLocations(locations);
        setSuggestions(
            locations.map(s => ({
                locationName: s.locationName,
                locationId: s.locationId,
                postalCode: s.postalCode,
            }))
        );
    };

    const handleSelectionChanged = (
        e: never,
        newValue: LocationOption,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<LocationOption>
    ) => {
        if (!newValue) {
            onSelect(null, null);
            return;
        }

        if (newValue) {
            if (clearInputOnAdd) {
                setLocationSearchText("");
                setSuggestions([]);
            } else {
                setLocationSearchText(newValue.locationName);
                setSuggestions([newValue]);
            }
            const location = locations.find(
                l => l.locationId === newValue.locationId
            );
            onSelect(newValue, location);
        }
    };

    const handleOnBlur = () => {
        if (!selectedLocation) {
            setSuggestions(props.initialSuggestions ?? []);
        }
        if (clearInputOnAdd) {
            setLocationSearchText("");
            setSuggestions([]);
        }
    };

    const extendedId = `${props.id}LocationSearch`;

    return (
        <div
            className={classnames(classes.root, className)}
            id={`${extendedId}Container`}
        >
            <SelectInput
                mode="suggest"
                clearable={!disableClearable}
                error={error}
                getOptionLabel={o => o.locationName ?? ""}
                getOptionSelected={(o, v) => o.locationId === v.locationId}
                helperText={helperText}
                id={extendedId}
                label={label}
                noOptionsText="Your keywords did not match any location"
                onChange={handleSelectionChanged}
                onInputChange={handleInputChange}
                options={suggestions}
                popupIcon={null}
                value={selectedLocation}
                inputValue={locationSearchText}
                onBlur={handleOnBlur}
            />
            {!!selectedLocation && !!props.onDistanceChange ? (
                <SelectInput
                    mode="list"
                    label="distance"
                    data-cy="location-distance"
                    options={LocationDistance.asArray()}
                    getOptionLabel={option => option.description ?? ""}
                    onChange={(e, v: LocationDistance) =>
                        onDistanceChange(v.value)
                    }
                    defaultValue={
                        props.searchRadiusInMeters
                            ? LocationDistance.asArray().find(
                                  d => d.value === props.searchRadiusInMeters
                              )
                            : LocationDistance[0]
                    }
                />
            ) : null}
        </div>
    );
};

export const LocationSearchField = (
    props: LocationSearchProps & { name: string }
) => {
    const [field, meta] = useField(props.name);
    return (
        <LocationSearch
            {...props}
            {...field}
            error={!!meta.error}
            helperText={meta.error}
        />
    );
};

class LocationDistance {
    id: number;
    readonly value: number;
    description: string;

    constructor(id: number, value: number, description: string) {
        this.description = description;
        this.value = value;
    }

    public static Exact = new LocationDistance(0, 0, "exact location only");
    public static FiveMiles = new LocationDistance(
        5,
        5 * 1610,
        "within 5 miles"
    );
    public static TenMiles = new LocationDistance(
        10,
        10 * 1610,
        "within 10 miles"
    );
    public static TwentyFiveMiles = new LocationDistance(
        25,
        25 * 1610,
        "within 25 miles"
    );
    public static FiftyMiles = new LocationDistance(
        50,
        50 * 1610,
        "within 50 miles"
    );
    public static OneHundredMiles = new LocationDistance(
        100,
        100 * 1610,
        "within 100 miles"
    );
    public static TwoHundredFiftyMiles = new LocationDistance(
        250,
        250 * 1610,
        "within 250 miles"
    );

    public static asArray = () => [
        LocationDistance.Exact,
        LocationDistance.FiveMiles,
        LocationDistance.TenMiles,
        LocationDistance.TwentyFiveMiles,
        LocationDistance.FiftyMiles,
        LocationDistance.OneHundredMiles,
        LocationDistance.TwoHundredFiftyMiles,
    ];

    public static fromId = (id: number) =>
        LocationDistance.asArray().find(ld => ld.id === id);
}
